import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { tokenStore } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadMe = useCallback(async () => {
    const token = tokenStore.get();
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      tokenStore.clear();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    tokenStore.set(data.access_token);
    // Fetch complete user data including visible_modules
    await loadMe();
    return data.user;
  };

  const logout = () => {
    tokenStore.clear();
    setUser(null);
    window.location.href = "/login";
  };

  const changePassword = async (newPassword) => {
    await api.post("/auth/change-password", { new_password: newPassword });
    await loadMe();
  };

  const hasRole = (...roles) => user && roles.includes(user.role);
  const canWrite = () => hasRole("owner", "td");
  // Read-along visibility. owner/td always see everything (backend returns all
  // modules in visible_modules for them); management is filtered per the
  // owner's Meekijken settings. Never bake this into the token.
  const canView = (module) => {
    if (!user) return false;
    if (user.role === "owner" || user.role === "td") return true;
    return (user.visible_modules || []).includes(module);
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, login, logout, changePassword, hasRole, canWrite, canView, refresh: loadMe }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
